(() => {
  'use strict';

  const STORAGE_KEY = 'kc_product_compare_v1';
  const DEFAULT_MAX_SAVED = 6;
  const MAX_DIRECT_COMPARE = 3;
  const shopRoot = window.Shopify?.routes?.root || '/';
  const productCache = new Map();

  const escapeHtml = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const readSelection = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];

      let directCompareCount = 0;

      return parsed
        .filter((item) => item && typeof item.handle === 'string' && item.handle.trim() !== '')
        .map((item) => {
          const compare = Boolean(item.compare) && directCompareCount < MAX_DIRECT_COMPARE;
          if (compare) directCompareCount += 1;

          return {
            ...item,
            handle: item.handle.trim(),
            compare,
          };
        });
    } catch (error) {
      console.warn('KC Produktvergleich: Auswahl konnte nicht gelesen werden.', error);
      return [];
    }
  };

  const writeSelection = (items) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(
      new CustomEvent('kc:compare:updated', {
        detail: { items },
      })
    );
  };

  const getMaxSaved = () => {
    const portalRoot = document.querySelector('[data-kc-compare-root]');
    const configured = Number(portalRoot?.dataset.kcMaxProducts || DEFAULT_MAX_SAVED);
    return Number.isFinite(configured) && configured >= 2 ? configured : DEFAULT_MAX_SAVED;
  };

  const getProductFromButton = (button) => {
    const wrapper = button.closest('[data-kc-compare-button-wrap]');
    const script = wrapper?.querySelector('[data-kc-compare-product]');

    if (!script) return null;

    try {
      const product = JSON.parse(script.textContent || '{}');
      if (!product.handle) return null;
      return product;
    } catch (error) {
      console.error('KC Produktvergleich: Produktdaten sind ungültig.', error);
      return null;
    }
  };

  const setButtonMessage = (button, message) => {
    const status = button
      .closest('[data-kc-compare-button-wrap]')
      ?.querySelector('[data-kc-compare-button-status]');

    if (!status) return;
    status.textContent = message;

    window.setTimeout(() => {
      if (status.textContent === message) status.textContent = '';
    }, 3500);
  };

  const updateButtons = (scope = document) => {
    const selectedHandles = new Set(readSelection().map((item) => item.handle));

    scope.querySelectorAll('[data-kc-compare-add]').forEach((button) => {
      const product = getProductFromButton(button);
      if (!product) return;

      const isSelected = selectedHandles.has(product.handle);
      const label = button.querySelector('[data-kc-compare-button-label]');

      button.setAttribute('aria-pressed', String(isSelected));
      button.classList.toggle('is-selected', isSelected);

      if (label) {
        label.textContent = isSelected
          ? button.dataset.selectedLabel || 'Ausgewählt ✓'
          : button.dataset.defaultLabel || 'Zur Auswahl';
      }
    });
  };

  const toggleProduct = (button) => {
    const product = getProductFromButton(button);
    if (!product) return;

    const items = readSelection();
    const existingIndex = items.findIndex((item) => item.handle === product.handle);

    if (existingIndex >= 0) {
      items.splice(existingIndex, 1);
      writeSelection(items);
      setButtonMessage(button, 'Aus der Auswahl entfernt.');
      return;
    }

    const maxSaved = getMaxSaved();
    if (items.length >= maxSaved) {
      setButtonMessage(button, `Maximal ${maxSaved} Produkte können gespeichert werden.`);
      return;
    }

    const compareCount = items.filter((item) => item.compare).length;

    items.push({
      ...product,
      compare: compareCount < MAX_DIRECT_COMPARE,
      addedAt: Date.now(),
    });

    writeSelection(items);
    setButtonMessage(button, 'Zur Auswahl hinzugefügt.');
  };

  const initButtons = (scope = document) => {
    scope.querySelectorAll('[data-kc-compare-add]').forEach((button) => {
      if (button.dataset.kcCompareInitialized === 'true') return;

      button.dataset.kcCompareInitialized = 'true';
      button.addEventListener('click', () => toggleProduct(button));
    });

    updateButtons(scope);
  };

  const fetchProduct = async (handle) => {
    if (productCache.has(handle)) return productCache.get(handle);

    const promise = fetch(`${shopRoot}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`Produkt ${handle} konnte nicht geladen werden.`);
      }
      return response.json();
    });

    productCache.set(handle, promise);
    return promise;
  };

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'de-DE';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2).replace('.', ',')} €`;
    }
  };

  const enrichSelection = async (items) => {
    const results = await Promise.allSettled(items.map((item) => fetchProduct(item.handle)));

    return items.map((item, index) => {
      const result = results[index];
      if (result.status !== 'fulfilled') return item;

      const live = result.value;

      return {
        ...item,
        id: live.id,
        title: live.title || item.title,
        url: live.url || item.url,
        image: live.featured_image || item.image,
        price: live.price,
        priceText: formatMoney(live.price),
        productType: item.productType || live.type,
        available: live.available,
        options: live.options || [],
        variants: live.variants || [],
      };
    });
  };

  const getDisplayValue = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return '–';
    return String(value).trim();
  };

  const savedCardMarkup = (item) => {
    const image = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.imageAlt || item.title)}" loading="lazy" width="600" height="750">`
      : '<div class="kc-compare-card__image-placeholder" aria-hidden="true"></div>';

    return `
      <article class="kc-compare-card" data-kc-saved-card="${escapeHtml(item.handle)}">
        <a class="kc-compare-card__image" href="${escapeHtml(item.url)}">${image}</a>
        <div class="kc-compare-card__body">
          <p class="kc-compare-card__type">${escapeHtml(getDisplayValue(item.productType))}</p>
          <h4 class="kc-compare-card__title">
            <a href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
          </h4>
          <p class="kc-compare-card__price">${escapeHtml(item.priceText || '')}</p>
          <label class="kc-compare-card__check">
            <input
              type="checkbox"
              data-kc-compare-toggle="${escapeHtml(item.handle)}"
              ${item.compare ? 'checked' : ''}
            >
            <span>Direkt vergleichen</span>
          </label>
          <div class="kc-compare-card__actions">
            <a class="button button--secondary" href="${escapeHtml(item.url)}">Produkt ansehen</a>
            <button type="button" class="kc-compare-card__remove" data-kc-remove="${escapeHtml(item.handle)}">
              Entfernen
            </button>
          </div>
        </div>
      </article>
    `;
  };

  const uniqueOptionValues = (product, optionIndex) => {
    const values = [];
    product.variants?.forEach((variant) => {
      const value = variant.options?.[optionIndex];
      if (value && !values.includes(value)) values.push(value);
    });
    return values;
  };
const getOptionLabel = (name, optionIndex) => {
  const normalized = String(name || '').trim().toLowerCase();

  if (['colour', 'color', 'farbe'].includes(normalized)) return 'Farbe';
  if (['size', 'größe', 'groesse'].includes(normalized)) return 'Größe';

  return name || `Option ${optionIndex + 1}`;
};
  const variantControlsMarkup = (product) => {
    if (!Array.isArray(product.variants) || product.variants.length === 0) {
      return '<p class="kc-compare-variant__status">Varianten derzeit nicht verfügbar.</p>';
    }

    const firstAvailable = product.variants.find((variant) => variant.available) || product.variants[0];

    const selects = (product.options || []).map((option, optionIndex) => {
  const values = uniqueOptionValues(product, optionIndex);
  const optionLabel = getOptionLabel(option.name, optionIndex);

  const isSize =
    ['größe', 'groesse', 'size'].includes(
      String(option.name || '').trim().toLowerCase()
    );

  const requireExplicitSelection =
    isSize && values.length > 1;

  const selectedValue =
    requireExplicitSelection
      ? ''
      : firstAvailable.options?.[optionIndex];

  return `
    <label class="kc-compare-variant__field">
      <span>${escapeHtml(optionLabel)}</span>

      <select data-kc-option-index="${optionIndex}">
        ${
          requireExplicitSelection
            ? '<option value="" selected disabled>Größe wählen</option>'
            : ''
        }

        ${values
          .map(
            (value) =>
              `<option
                value="${escapeHtml(value)}"
                ${
                  value === selectedValue
                    ? 'selected'
                    : ''
                }
              >
                ${escapeHtml(value)}
              </option>`
          )
          .join('')}
      </select>
    </label>
  `;
});

    return `
      <div class="kc-compare-variant" data-kc-variant-card="${escapeHtml(product.handle)}">
        ${selects.join('')}
        <p class="kc-compare-variant__price" data-kc-variant-price></p>
        <p class="kc-compare-variant__status" data-kc-variant-status aria-live="polite"></p>
        <button type="button" class="button kc-compare-variant__add" data-kc-add-cart>
          Dieses Teil sichern
        </button>
      </div>
    `;
  };

  const comparisonMarkup = (items) => {
    const rows = [
      ['Preis', (item) => item.priceText],
      ['Markenwelt', (item) => item.brandWorld],
      ['Produktart', (item) => item.productType],
      ['Stoffgewicht', (item) => item.weight],
      ['Passform', (item) => item.fit],
      ['Material', (item) => item.material],
      ['Tragegefühl', (item) => item.feel],
      ['Gestaltung', (item) => item.design],
    ];

    const productHeads = items
      .map(
        (item) => `
          <div class="kc-compare-table__product">
            ${
              item.image
                ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.imageAlt || item.title)}" loading="lazy" width="480" height="600">`
                : ''
            }
            <strong>${escapeHtml(item.title)}</strong>
          </div>
        `
      )
      .join('');

    const rowMarkup = rows
      .map(([label, getter]) => {
        const cells = items
          .map((item) => `<div class="kc-compare-table__cell">${escapeHtml(getDisplayValue(getter(item)))}</div>`)
          .join('');

        return `<div class="kc-compare-table__label">${escapeHtml(label)}</div>${cells}`;
      })
      .join('');

    const variantCells = items
      .map((item) => `<div class="kc-compare-table__cell kc-compare-table__cell--actions">${variantControlsMarkup(item)}</div>`)
      .join('');

    return `
      <div class="kc-compare-table-grid" style="--kc-compare-columns:${items.length}">
        <div class="kc-compare-table__label kc-compare-table__label--top">Produkt</div>
        ${productHeads}
        ${rowMarkup}
        <div class="kc-compare-table__label">Auswahl</div>
        ${variantCells}
      </div>
    `;
  };

  const setPortalStatus = (root, message) => {
    const status = root.querySelector('[data-kc-compare-status]');
    if (!status) return;

    status.textContent = message;
    if (!message) return;

    window.setTimeout(() => {
      if (status.textContent === message) status.textContent = '';
    }, 4500);
  };

  const getSelectedVariant = (card, product) => {
  const selectedOptions = Array.from(
    card.querySelectorAll('[data-kc-option-index]')
  ).map((select) => select.value);

  if (selectedOptions.some((value) => !value)) {
    return null;
  }

  return product.variants?.find((variant) =>
    selectedOptions.every(
      (value, index) =>
        variant.options?.[index] === value
    )
  );
};

  const updateVariantCard = (root, card) => {
    const product = root._kcProducts?.get(card.dataset.kcVariantCard);
    if (!product) return;

    const optionSelects = Array.from(card.querySelectorAll('[data-kc-option-index]'));
    const hasMissingSelection = optionSelects.some((select) => !select.value);
    const variant = hasMissingSelection ? null : getSelectedVariant(card, product);

    const addButton = card.querySelector('[data-kc-add-cart]');
    const status = card.querySelector('[data-kc-variant-status]');
    const price = card.querySelector('[data-kc-variant-price]');

    if (price) price.textContent = variant ? formatMoney(variant.price) : '';

    if (status) {
      status.textContent = hasMissingSelection
        ? 'Bitte Variante auswählen.'
        : !variant
          ? 'Diese Kombination ist nicht verfügbar.'
          : variant.available
            ? 'Verfügbar'
            : 'Derzeit nicht verfügbar';
    }

    if (addButton) {
      addButton.disabled = !variant?.available;
      addButton.textContent = hasMissingSelection
        ? 'Größe wählen'
        : variant?.available
          ? 'Dieses Teil sichern'
          : 'Derzeit nicht verfügbar';

      if (variant?.available) {
        addButton.dataset.variantId = String(variant.id);
      } else {
        delete addButton.dataset.variantId;
      }
    }
  };

  const hydrateVariantCards = (root) => {
    root.querySelectorAll('[data-kc-variant-card]').forEach((card) => updateVariantCard(root, card));
  };

  const addVariantToCart = async (root, button) => {
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    button.disabled = true;
    const originalLabel = button.textContent;
    button.textContent = 'Wird hinzugefügt …';

    try {
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'Das Produkt konnte nicht hinzugefügt werden.');
      }

      window.location.assign(`${shopRoot}cart`);
    } catch (error) {
      setPortalStatus(root, error.message || 'Das Produkt konnte nicht hinzugefügt werden.');
      button.disabled = false;
      button.textContent = originalLabel;
    }
  };

  const renderPortal = async (root) => {
    const renderToken = String(Date.now() + Math.random());
    root.dataset.kcRenderToken = renderToken;

    const maxProducts = Number(root.dataset.kcMaxProducts || DEFAULT_MAX_SAVED);
    const storedItems = readSelection().slice(0, maxProducts);
    const empty = root.querySelector('[data-kc-compare-empty]');
    const content = root.querySelector('[data-kc-compare-content]');
    const saved = root.querySelector('[data-kc-compare-saved]');
    const count = root.querySelector('[data-kc-selection-count]');
    const comparison = root.querySelector('[data-kc-comparison]');
    const table = root.querySelector('[data-kc-compare-table]');

    if (count) count.textContent = String(storedItems.length);

    if (storedItems.length === 0) {
      if (empty) empty.hidden = false;
      if (content) content.hidden = true;
      if (saved) saved.innerHTML = '';
      if (comparison) comparison.hidden = true;
      if (table) table.innerHTML = '';
      root._kcProducts = new Map();
      return;
    }

    if (empty) empty.hidden = true;
    if (content) content.hidden = false;
    if (saved) saved.innerHTML = '<p class="kc-compare__loading">Auswahl wird geladen …</p>';

    const items = await enrichSelection(storedItems);
    if (root.dataset.kcRenderToken !== renderToken) return;

    root._kcProducts = new Map(items.map((item) => [item.handle, item]));

    if (saved) saved.innerHTML = items.map(savedCardMarkup).join('');

    const directItems = items.filter((item) => item.compare).slice(0, MAX_DIRECT_COMPARE);

    if (directItems.length >= 2) {
      if (comparison) comparison.hidden = false;
      if (table) table.innerHTML = comparisonMarkup(directItems);
      hydrateVariantCards(root);
    } else {
      if (comparison) comparison.hidden = true;
      if (table) table.innerHTML = '';
    }
  };

  const initPortal = (root) => {
    if (!root || root.dataset.kcCompareInitialized === 'true') return;
    root.dataset.kcCompareInitialized = 'true';

    root.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-kc-remove]');
      if (removeButton) {
        const handle = removeButton.dataset.kcRemove;
        writeSelection(readSelection().filter((item) => item.handle !== handle));
        return;
      }

      const clearButton = event.target.closest('[data-kc-compare-clear]');
      if (clearButton) {
        writeSelection([]);
        setPortalStatus(root, 'Auswahl geleert.');
        return;
      }

      const addCartButton = event.target.closest('[data-kc-add-cart]');
      if (addCartButton) {
        addVariantToCart(root, addCartButton);
      }
    });

    root.addEventListener('change', (event) => {
      const compareToggle = event.target.closest('[data-kc-compare-toggle]');
      if (compareToggle) {
        const handle = compareToggle.dataset.kcCompareToggle;
        const items = readSelection();
        const item = items.find((entry) => entry.handle === handle);
        if (!item) return;

        if (compareToggle.checked) {
          const activeCount = items.filter((entry) => entry.compare).length;
          if (activeCount >= MAX_DIRECT_COMPARE) {
            compareToggle.checked = false;
            setPortalStatus(root, 'Maximal drei Produkte können direkt verglichen werden.');
            return;
          }
        }

        item.compare = compareToggle.checked;
        writeSelection(items);
        return;
      }

      const variantSelect = event.target.closest('[data-kc-option-index]');
      if (variantSelect) {
        const card = variantSelect.closest('[data-kc-variant-card]');
        if (card) updateVariantCard(root, card);
      }
    });

    renderPortal(root);
  };

  const initAll = (scope = document) => {
    initButtons(scope);
    scope.querySelectorAll('[data-kc-compare-root]').forEach(initPortal);
  };

  document.addEventListener('DOMContentLoaded', () => initAll(document));

  window.addEventListener('kc:compare:updated', () => {
    updateButtons(document);
    document.querySelectorAll('[data-kc-compare-root]').forEach(renderPortal);
  });

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
